import {
  detectDesktopPlatform,
  type DesktopPlatform,
} from "@volca/ui/lib/desktop-platform";
import type {
  AiSpreadsheetCellKind,
  AiSpreadsheetValueType,
  IManualRowInput,
} from "@/payroll/ai-script/types";
import type {
  WorkbookHistoryEntry,
  WorkbookHistoryOp,
} from "./workbook-history-types";

// Pure, side-effect-free helpers behind the workbook editor's undo/redo. The
// viewer captures a gesture's prior state, turns it into a WorkbookHistoryEntry
// with these builders, and later hands each op back to `replayOp` to (re-)apply
// it against the API. Keeping this logic here (not in the 1000-line viewer) lets
// the grouping + inversion rules be unit-tested in isolation.

// ---- Entry builders -------------------------------------------------------

interface CellEditGesture {
  sheetId: string;
  cellId: string;
  // The A1 reference for the label ("Edit cell B4"); omit for a bare label.
  coordinate?: string;
  // The raw input before/after the edit. Undo replays the prior, redo the next.
  priorRawInput: string;
  nextRawInput: string;
  priorKind: AiSpreadsheetCellKind;
  nextKind: AiSpreadsheetCellKind;
}

// A single in-grid / formula-bar cell edit. Forward re-applies the new input,
// inverse restores what was there before.
export const buildCellEditEntry = ({
  sheetId,
  cellId,
  coordinate,
  priorRawInput,
  nextRawInput,
  priorKind,
  nextKind,
}: CellEditGesture): WorkbookHistoryEntry => ({
  label: coordinate ? `Edit cell ${coordinate}` : "Edit cell",
  forward: [
    {
      type: "cellEdit",
      sheetId,
      cellId,
      rawInput: nextRawInput,
      kind: nextKind,
    },
  ],
  inverse: [
    {
      type: "cellEdit",
      sheetId,
      cellId,
      rawInput: priorRawInput,
      kind: priorKind,
    },
  ],
});

interface FormatEditGesture {
  sheetId: string;
  cellId: string;
  coordinate?: string;
  priorValueType: string;
  nextValueType: string;
}

// A "Format cell" reformat. Forward sets the new value type, inverse restores
// the prior one; the cell's raw input is unchanged (the replayer supplies the
// current input, see ReplayOpMutations.resolveCellRawInput).
export const buildFormatEditEntry = ({
  sheetId,
  cellId,
  coordinate,
  priorValueType,
  nextValueType,
}: FormatEditGesture): WorkbookHistoryEntry => ({
  label: coordinate ? `Format cell ${coordinate}` : "Format cell",
  forward: [{ type: "formatEdit", sheetId, cellId, valueType: nextValueType }],
  inverse: [{ type: "formatEdit", sheetId, cellId, valueType: priorValueType }],
});

interface ManualRowCreateGesture {
  sheetId: string;
  // The row payload(s) the create posted, so a redo re-creates them.
  rows: IManualRowInput[];
  // The server-assigned id(s) of the created row(s), so an undo deletes them.
  manualRowIds: string[];
  label?: string;
}

// A manual-row add (append / insert / typed-into-blank-row). Forward re-creates
// from the same payload; inverse deletes the created row(s).
export const buildManualRowCreateEntry = ({
  sheetId,
  manualRowIds,
  label = "Add row",
}: ManualRowCreateGesture): WorkbookHistoryEntry => ({
  label,
  // The row is created by the original gesture; this entry only drives undo/redo.
  // Undo soft-deletes the created rows; redo RESTORES them by their original ids
  // (a delete is soft, so ids round-trip) rather than creating new rows — else
  // redo would mint fresh ids and the entry's inverse would target dead ones.
  forward: manualRowIds.map((manualRowId) => ({
    type: "manualRowRestore",
    sheetId,
    manualRowId,
  })),
  inverse: manualRowIds.map((manualRowId) => ({
    type: "manualRowDelete",
    sheetId,
    manualRowId,
  })),
});

interface ManualRowDeleteGesture {
  sheetId: string;
  manualRowId: string;
  label?: string;
}

// A manual-row delete. Forward deletes again; inverse restores the same row
// (deletes are soft, so the id round-trips — undo/redo of a delete is stable).
export const buildManualRowDeleteEntry = ({
  sheetId,
  manualRowId,
  label = "Delete row",
}: ManualRowDeleteGesture): WorkbookHistoryEntry => ({
  label,
  forward: [{ type: "manualRowDelete", sheetId, manualRowId }],
  inverse: [{ type: "manualRowRestore", sheetId, manualRowId }],
});

interface PasteCellEdit {
  cellId: string;
  priorRawInput: string;
  nextRawInput: string;
  priorKind: AiSpreadsheetCellKind;
  nextKind: AiSpreadsheetCellKind;
  // The clipboard-sourced type of the pasted value (redo) and the cell's type
  // before the paste (undo), so replay preserves number/currency/percent
  // formatting instead of re-deriving it from raw_input.
  priorValueType?: AiSpreadsheetValueType;
  nextValueType?: AiSpreadsheetValueType;
}

interface PasteGesture {
  sheetId: string;
  // The existing cells the paste overwrote, each with its prior + new input.
  cellEdits: PasteCellEdit[];
  // The server ids of any rows the paste added past the data boundary.
  createdRowIds?: string[];
  label?: string;
}

// A paste is a SINGLE undo unit even though it touches many cells. The cell
// changes are re-applied / reverted through the atomic bulk-paste endpoint in ONE
// request each way (not one request per cell), so undoing a large paste is a
// single round-trip + recalculation. Any rows the paste added are soft-deleted on
// undo and restored by id on redo (their ids round-trip), so no rows are
// re-created with new ids.
export const buildPasteEntry = ({
  sheetId,
  cellEdits,
  createdRowIds,
  label = "Paste",
}: PasteGesture): WorkbookHistoryEntry => {
  const forward: WorkbookHistoryOp[] = [];
  const inverse: WorkbookHistoryOp[] = [];

  if (cellEdits.length > 0) {
    forward.push({
      type: "bulkCells",
      sheetId,
      edits: cellEdits.map((edit) => ({
        cell_id: edit.cellId,
        raw_input: edit.nextRawInput,
        kind: edit.nextKind,
        ...(edit.nextValueType ? { value_type: edit.nextValueType } : {}),
      })),
    });
    inverse.push({
      type: "bulkCells",
      sheetId,
      edits: cellEdits.map((edit) => ({
        cell_id: edit.cellId,
        raw_input: edit.priorRawInput,
        kind: edit.priorKind,
        ...(edit.priorValueType ? { value_type: edit.priorValueType } : {}),
      })),
    });
  }

  // Rows the paste created are restored/deleted in ONE bulk request each way, so
  // undoing a paste that spilled into 30 new rows isn't 30 sequential requests.
  const rowIds = createdRowIds ?? [];
  if (rowIds.length > 0) {
    forward.push({ type: "bulkRowsRestore", sheetId, manualRowIds: rowIds });
    inverse.push({ type: "bulkRowsDelete", sheetId, manualRowIds: rowIds });
  }

  return { label, forward, inverse };
};

// ---- Keyboard shortcut matching -------------------------------------------

export type UndoRedoAction = "undo" | "redo";
export type UndoRedoPlatform = DesktopPlatform;

// Just the modifier/key fields we read, so callers (and tests) can pass a plain
// object without a full KeyboardEvent.
export type UndoRedoKeyEvent = Pick<
  KeyboardEvent,
  "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey"
>;

// Classify a keydown as undo, redo, or neither, per platform convention:
//   mac:        Cmd+Z (undo), Cmd+Shift+Z (redo)
//   win/linux:  Ctrl+Z (undo), Ctrl+Shift+Z / Ctrl+Y (redo)
// The primary modifier must be the platform's own (Cmd on mac, Ctrl elsewhere)
// and the other must NOT be held, so a stray cross-modifier chord is ignored.
export const matchesUndoRedoShortcut = (
  event: UndoRedoKeyEvent,
  platform: UndoRedoPlatform = detectDesktopPlatform(),
): UndoRedoAction | null => {
  if (event.altKey) return null;

  const primaryHeld = platform === "mac" ? event.metaKey : event.ctrlKey;
  const otherHeld = platform === "mac" ? event.ctrlKey : event.metaKey;

  if (!primaryHeld || otherHeld) return null;

  const key = event.key.toLowerCase();

  if (key === "z") return event.shiftKey ? "redo" : "undo";
  // Ctrl+Y is a Windows/Linux-only redo; unshifted only.
  if (key === "y" && platform !== "mac" && !event.shiftKey) return "redo";

  return null;
};
